import React, { useState, useEffect, useMemo, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardActionArea,
  LinearProgress,
  Skeleton,
  useMediaQuery,
  ThemeProvider,
  createTheme,
  Drawer,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Tooltip,
  Fade,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SendIcon from "@mui/icons-material/Send";
import { toast } from "react-toastify";
import { bonusApi } from "../../Components/bonusApi";
import { useTheme } from "../../hooks/useTheme";
import { createMuiThemeFromMode } from "../../theme/theme";
import { useListViewMode } from "../../hooks/useListPreferences";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { GlobalSearchBar } from "../../Components/ERPList";
import { RoleContext } from "../../Contexts/RoleContext";
import CreateBonusWizardModal from "./CreateBonusWizardModal";
import styles from "../AllEnquiries.module.scss";

const baseThemeOptions = {
  palette: {
    secondary: { main: "#f5365c" },
    success: { main: "#2dce89" },
    warning: { main: "#fb6340" },
    info: { main: "#11cdef" },
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    h4: { fontWeight: 600, letterSpacing: "-0.02em" },
    button: { textTransform: "none", fontWeight: 500 },
  },
  shape: { borderRadius: 12 },
};

const StyledCard = styled(Card)(({ theme }) => ({
  cursor: "pointer",
  transition: "transform 0.2s, box-shadow 0.2s",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.shadows[10],
  },
}));

const FilterPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  backgroundColor: theme.palette.mode === "dark" ? undefined : "#ffffff",
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
}));

const BONUS_STATUSES = [
  { value: "", label: "All statuses" },
  { value: "draft", label: "Draft" },
  { value: "pending_approval", label: "Pending approval" },
  { value: "approved", label: "Approved" },
  { value: "active", label: "Active" },
  { value: "successful", label: "Successful" },
  { value: "unsuccessful", label: "Unsuccessful" },
  { value: "cancelled", label: "Cancelled" },
];

const LIST_ID = "bonuses";

function BonusesPage() {
  const { themeMode } = useTheme();
  const theme = useMemo(
    () => createTheme(createMuiThemeFromMode(themeMode, baseThemeOptions)),
    [themeMode]
  );
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const { role } = useContext(RoleContext);
  const [bonuses, setBonuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useListViewMode(LIST_ID, "table");
  const [globalSearch, setGlobalSearch] = useState("");
  const debouncedSearch = useDebouncedValue(globalSearch, 400);
  const [statusFilter, setStatusFilter] = useState("");
  const [filterOpen, setFilterOpen] = useState(!isMobile);
  const [approveLoading, setApproveLoading] = useState(null);
  const [createWizardOpen, setCreateWizardOpen] = useState(false);

  const fetchBonuses = async () => {
    setLoading(true);
    try {
      const data = await bonusApi.list({
        search: debouncedSearch.trim() || undefined,
        status: statusFilter || undefined,
      });
      setBonuses(data.bonuses || []);
    } catch (err) {
      toast.error(err?.message || "Failed to load bonuses");
      setBonuses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBonuses();
  }, [debouncedSearch, statusFilter]);

  const handleApprove = async (e, b) => {
    e.stopPropagation();
    if (b.status !== "pending_approval" || role !== "admin") return;
    setApproveLoading(b.id);
    try {
      await bonusApi.updateStatus(b.id, "active");
      toast.success(`"${b.name}" approved and now active.`);
      fetchBonuses();
    } catch (err) {
      toast.error(err?.message || "Failed to approve");
    } finally {
      setApproveLoading(null);
    }
  };

  const handleSubmitForApproval = async (e, b) => {
    e.stopPropagation();
    if (b.status !== "draft") return;
    const allowed = ["admin", "Manager"];
    if (!allowed.includes(role)) return;
    setApproveLoading(b.id);
    try {
      await bonusApi.updateStatus(b.id, "pending_approval");
      toast.success(`"${b.name}" submitted for approval.`);
      fetchBonuses();
    } catch (err) {
      toast.error(err?.message || "Failed to submit");
    } finally {
      setApproveLoading(null);
    }
  };

  const resetFilters = () => {
    setGlobalSearch("");
    setStatusFilter("");
    toast.info("Filters cleared.");
  };

  const toggleViewMode = () => {
    setViewMode((prev) => (prev === "cards" ? "table" : "cards"));
  };

  const formatProgress = (b) => {
    if (b.metric === "conversion_rate") return `${Number(b.current_progress).toFixed(1)}%`;
    if (b.metric === "revenue") return Number(b.current_progress).toLocaleString();
    return String(Math.round(b.current_progress));
  };

  const formatGoal = (b) => {
    if (b.metric === "conversion_rate") return `${Number(b.goal).toFixed(0)}%`;
    if (b.metric === "revenue") return Number(b.goal).toLocaleString();
    return String(Math.round(b.goal));
  };

  const getStatusColor = (status) => {
    const map = {
      draft: "default",
      pending_approval: "warning",
      approved: "info",
      active: "success",
      successful: "success",
      unsuccessful: "error",
      cancelled: "default",
    };
    return map[status] || "default";
  };

  const getStatusLabel = (status) => {
    const item = BONUS_STATUSES.find((s) => s.value === status);
    return item ? item.label : (status || "").replace(/_/g, " ");
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className={styles.root}>
        <Paper elevation={0} className={styles.contentPaper}>
              <Box sx={{ marginBottom: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
                  <Box>
                    <Typography variant="h4" className={styles.pageTitle}>
                      Office Bonuses
                    </Typography>
                    <Typography variant="body2" className={styles.pageSubtitle} sx={{ mt: 0.5 }}>
                      Manage shared office bonus challenges and track progress.
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setCreateWizardOpen(true)}
                  >
                    Create Bonus
                  </Button>
                </Box>
              </Box>

              <Box className={styles.header}>
                <GlobalSearchBar
                  value={globalSearch}
                  onChange={setGlobalSearch}
                  placeholder="Search name, description…"
                />
                <Box className={styles.headerActions}>
                  <Button
                    startIcon={<FilterListIcon />}
                    onClick={() => setFilterOpen(!filterOpen)}
                    variant="outlined"
                    size="small"
                  >
                    {filterOpen ? "Hide Filters" : "Show Filters"}
                  </Button>
                  <Tooltip title={viewMode === "cards" ? "Table view" : "Card view"}>
                    <IconButton onClick={toggleViewMode} color="primary">
                      {viewMode === "cards" ? <ViewListIcon /> : <ViewModuleIcon />}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {filterOpen && (
                <Fade in={filterOpen}>
                  <FilterPaper variant="outlined">
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Status</InputLabel>
                          <Select
                            name="status"
                            value={statusFilter}
                            label="Status"
                            onChange={(e) => setStatusFilter(e.target.value)}
                          >
                            {BONUS_STATUSES.map((opt) => (
                              <MenuItem key={opt.value || "all"} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} className={styles.filterActions}>
                        <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={resetFilters}>
                          Reset
                        </Button>
                      </Grid>
                    </Grid>
                  </FilterPaper>
                </Fade>
              )}

              {loading && (
                <Box className={styles.loadingContainer}>
                  {viewMode === "cards" ? (
                    <Grid container spacing={2}>
                      {[...Array(6)].map((_, i) => (
                        <Grid item xs={12} sm={6} md={4} key={i}>
                          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Box>
                      <Skeleton variant="rectangular" height={60} sx={{ mb: 1 }} />
                      <Skeleton variant="rectangular" height={400} />
                    </Box>
                  )}
                </Box>
              )}

              {!loading && bonuses.length === 0 && (
                <Box className={styles.emptyState}>
                  <EmojiEventsIcon sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    No bonuses found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Try adjusting filters or create a new bonus.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setCreateWizardOpen(true)}
                    sx={{ mt: 2 }}
                  >
                    Create Bonus
                  </Button>
                </Box>
              )}

              {!loading && bonuses.length > 0 && viewMode === "cards" && (
                <Grid container spacing={2} className={styles.cardsGrid}>
                  {bonuses.map((b) => (
                    <Grid item xs={12} sm={6} md={4} key={b.id}>
                      <StyledCard>
                        <CardActionArea onClick={() => navigate(`/bonuses/${b.id}`)}>
                          <CardHeader
                            avatar={
                              <Box sx={{ bgcolor: "primary.main", color: "primary.contrastText", p: 1, borderRadius: 2 }}>
                                <EmojiEventsIcon />
                              </Box>
                            }
                            title={b.name}
                            subheader={`${b.metric_label} · ${b.scope_display ?? "Overall"} · ${b.timeframe}`}
                            action={
                              <Chip
                                label={getStatusLabel(b.status)}
                                color={getStatusColor(b.status)}
                                size="small"
                                variant="outlined"
                              />
                            }
                            className={styles.cardHeader}
                          />
                          <CardContent className={styles.cardContent}>
                            <Typography variant="body2" color="text.secondary">
                              Scope: {b.scope_display ?? "Overall"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Goal: {formatGoal(b)}
                            </Typography>
                            <Typography variant="body2">
                              Progress: {formatProgress(b)} / {formatGoal(b)}
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(100, (Number(b.current_progress) / Number(b.goal)) * 100)}
                              sx={{ mt: 1, mb: 1, borderRadius: 1, height: 8 }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              Reward: {b.reward_description || b.reward_type}
                            </Typography>
                            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                              <Button
                                size="small"
                                startIcon={<VisibilityIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/bonuses/${b.id}`);
                                }}
                              >
                                View
                              </Button>
                              {(role === "admin" || role === "Manager") && b.status === "draft" && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<SendIcon />}
                                  disabled={approveLoading === b.id}
                                  onClick={(e) => handleSubmitForApproval(e, b)}
                                >
                                  {approveLoading === b.id ? "Submitting…" : "Submit for approval"}
                                </Button>
                              )}
                              {role === "admin" && b.status === "pending_approval" && (
                                <Button
                                  size="small"
                                  color="success"
                                  variant="contained"
                                  startIcon={<CheckCircleIcon />}
                                  disabled={approveLoading === b.id}
                                  onClick={(e) => handleApprove(e, b)}
                                >
                                  {approveLoading === b.id ? "Approving…" : "Approve"}
                                </Button>
                              )}
                            </Stack>
                          </CardContent>
                        </CardActionArea>
                      </StyledCard>
                    </Grid>
                  ))}
                </Grid>
              )}

              {!loading && bonuses.length > 0 && viewMode === "table" && (
                <TableContainer component={Paper} variant="outlined" className={styles.tableContainer}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Scope</TableCell>
                        <TableCell>Metric</TableCell>
                        <TableCell>Goal</TableCell>
                        <TableCell>Progress</TableCell>
                        <TableCell>Timeframe</TableCell>
                        <TableCell>Reward</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bonuses.map((b) => (
                        <TableRow
                          key={b.id}
                          hover
                          sx={{ cursor: "pointer" }}
                          onClick={() => navigate(`/bonuses/${b.id}`)}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {b.name}
                            </Typography>
                          </TableCell>
                          <TableCell>{b.scope_display ?? "Overall"}</TableCell>
                          <TableCell>{b.metric_label}</TableCell>
                          <TableCell>{formatGoal(b)}</TableCell>
                          <TableCell>
                            {formatProgress(b)} / {formatGoal(b)}
                          </TableCell>
                          <TableCell>{b.timeframe}</TableCell>
                          <TableCell>{b.reward_description || b.reward_type}</TableCell>
                          <TableCell>
                            <Chip
                              label={getStatusLabel(b.status)}
                              color={getStatusColor(b.status)}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Button
                                size="small"
                                startIcon={<VisibilityIcon />}
                                onClick={() => navigate(`/bonuses/${b.id}`)}
                              >
                                View
                              </Button>
                              {(role === "admin" || role === "Manager") && b.status === "draft" && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<SendIcon />}
                                  disabled={approveLoading === b.id}
                                  onClick={(e) => handleSubmitForApproval(e, b)}
                                >
                                  Submit for approval
                                </Button>
                              )}
                              {role === "admin" && b.status === "pending_approval" && (
                                <Button
                                  size="small"
                                  color="success"
                                  variant="contained"
                                  startIcon={<CheckCircleIcon />}
                                  disabled={approveLoading === b.id}
                                  onClick={(e) => handleApprove(e, b)}
                                >
                                  {approveLoading === b.id ? "…" : "Approve"}
                                </Button>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>

        <CreateBonusWizardModal
          open={createWizardOpen}
          onClose={() => setCreateWizardOpen(false)}
          onSuccess={() => fetchBonuses()}
        />
      </Box>
    </ThemeProvider>
  );
}

export default BonusesPage;
